import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { connect } from 'react-redux';

import { withStyles, Paper } from '@material-ui/core';

import BillingAccountForm from './BillingAccountForm';
import * as Yup from 'yup';
import * as actions from './store/billingAccountActions';
import History from '../../../History';
import LoadingDialog from './Details/LoadingDialog'
import {BackButton, hasPermission, parseURL} from '../../sc-utilities';
import { snackActions } from '../../sc-utilities/SnackBar';
import countries from 'i18n-iso-countries';
import { states, getStatesList, getCountryList } from '../../sc-utilities/CountriesAndUSstatesList';

const styles = theme => ({
    root: {
        width: 'auto',
        height: 'auto',
        padding: theme.spacing(2),
        margin: theme.spacing(2)
    },
    customTooltip: {
        marginLeft: theme.spacing(1),
        marginTop: '-2px'
    }
});

const billingModes = [
    { value: 'prepaid', displayValue: 'Prepaid' },
    { value: 'postpaid', displayValue: 'Contract' }
];

const accountStatuses = [
    { value: 'active', displayValue: 'Active' },
    { value: 'prepared', displayValue: 'Prepared' },
    { value: 'inactive', displayValue: 'Inactive' }
];

const termsOfPayment = [
    { value: 'dor', displayValue: 'Due on Receipt' },
    { value: 'pia', displayValue: 'Payment in advance' },
    { value: 'eom', displayValue: 'End of Month' },
    { value: 'cod', displayValue: 'Cash on Delivery' },
    { value: 'net7', displayValue: '7 days after invoice date' },
    { value: 'net10', displayValue: '10 days after invoice date' },
    { value: 'net15', displayValue: '15 days after invoice date' },
    { value: 'net30', displayValue: '30 days after invoice date' },
    { value: 'net60', displayValue: '60 days after invoice date' }
];

const languages = [
    { value: 'en', displayValue: 'English' },
    { value: 'de', displayValue: 'German' },
    { value: 'sr', displayValue: 'Serbian' }
];

const BillingAccountCreate = props => {
    const { activeCustomer, translations, onCreateBillingAccount, userLanguage, classes, onListCustomers, onListUsers, currentUserID, creating, permissions } = props;
    const [createData, setCreateData] = useState({ users: [], customers: [], loadingData: true })
    const searchParams = parseURL(window.location.search);
    const language = userLanguage || 'en';

    try {
        let lang = userLanguage;
        if (userLanguage.includes("zh-")) {
            lang = "zh";
        }
        countries.registerLocale(require(`i18n-iso-countries/langs/${lang}.json`));
    } catch (error) {
        console.log(error)
    }

    useEffect(() => {
        const fetchData = async () => {
            let customers = await onListCustomers({ ActiveCustomerID: activeCustomer });
            let users = await onListUsers({ ActiveCustomerID: activeCustomer });

            if (customers && customers.length === 0) {
                snackActions.snackBarComponent(
                    `${translations['need_at_least'] || 'You need at least one Customer'}. ${translations['please_create'] || 'Please create it'}.`,
                    'warning'
                );
                History.goBack();
            }
            setCreateData({ users: users, customers: customers, loadingData: false })
        }
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    let { users, customers } = createData

    const accountValue = currentUserID && users
        ? users.filter(displayValue => {
            return currentUserID === displayValue.value;
        })[0]
        : null;

    const submitHandler = async data => {
        const requestData = { ...data };

        if (props.CustomerID) {
            requestData['CustomerID'] = props.CustomerID;
        }
        const Address = {
            AddressLine1: requestData.AddressLine1,
            AddressLine2: requestData.AddressLine2,
            State: requestData.State?.key,
            City: requestData.City,
            Country: requestData.Country?.key
        };

        const Communication = {
            Language: requestData.Language,
            PhoneNumber: requestData.PhoneNumber,
            Email: requestData.Email
        };
        requestData['AccountantID'] = requestData['Accountant'] ? requestData['Accountant'].value : null;
        requestData['Address'] = Address;
        requestData['Communication'] = JSON.stringify(Communication);
        await onCreateBillingAccount({ ...requestData, ActiveCustomerID: activeCustomer });
        History.goBack();
    };

    let initialValues = {
        BillingMode: billingModes && billingModes.length > 0 ? billingModes[0].value : '',
        Status: accountStatuses && accountStatuses.length > 0 ? accountStatuses[0].value : '',
        ActivationDate: moment(),
        ExpiresAt: moment().add(1, 'years'),
        BillingDay: 1,
        CustomerOrder: '',
        VAT: '',
        TaxClassification: '',
        TermsOfPayment: termsOfPayment ? termsOfPayment[0].value : '',
        CustomerID: searchParams && searchParams.customerID ? searchParams.customerID : customers && customers.length > 0 ? customers[0].value : '',
        Accountant: accountValue ? accountValue : '',
        Language: languages ? languages[0].value : '',
        PhoneNumber: '',
        Email: '',
        CustomerAddress: false,
        AddressLine1: '',
        AddressLine2: '',
        State: null,
        City: '',
        Country: ''
    };

    let formSettings = {
        Validation: {
            BillingMode: Yup.string().required('Required'),
            Status: Yup.string().required('Required'),
            ActivationDate: Yup.date()
                .required(),
            ExpiresAt: Yup.date()
                .required(),
            BillingDay: Yup.number().required('Required').min(1).max(28),
            CustomerOrder: Yup.string().notRequired('Required'),
            VAT: Yup.string().notRequired('Required'),
            TaxClassification: Yup.string().notRequired('Required'),
            TermsOfPayment: Yup.string().notRequired('Required'),
            CustomerID: Yup.string().notRequired('Required'),
            Accountant: Yup.string().notRequired('Required'),
            Language: Yup.string().required('Required'),
            PhoneNumber: Yup.string().notRequired('Required'),
            Email: Yup.string().email().notRequired('Required'),
            CustomerAddress: Yup.boolean().notRequired(),
            AddressLine1: Yup.string().required('Required'),
            AddressLine2: Yup.string().notRequired(),
            City: Yup.string().required('Required'),
            State: Yup.string().when("Country", {
                is: "United States of America",
                then: Yup.string().required()
            }),
            Country: Yup.string().required('Required')
        },
        FormTitle: translations['create_billing_account'] || 'Create billing account',
        ButtonSubmit:  hasPermission(permissions, 'billing_account_create') ?  {
            Title: translations['save'] || 'SAVE',
            Color: 'primary'
        } : null,
    };

    return createData.loadingData ? <LoadingDialog /> :
        <Paper className={classes.root}>
            {BackButton(translations, classes.customTooltip)}
            <BillingAccountForm
                initialValues={initialValues}
                countryList={getCountryList(countries, language)}
                usStatesList={getStatesList(states)}
                translations={translations}
                formSettings={formSettings}
                billingModes={billingModes}
                accountStatuses={accountStatuses}
                termsOfPayment={termsOfPayment}
                customers={customers}
                users={users}
                disabledCustomer={!!(searchParams && searchParams.customerID)}
                languages={languages}
                handleSubmit={submitHandler}
                loading={creating}
                />
        </Paper>

};

const mapStateToProps = state => {
    return {
        activeCustomer: state?.core?.login?.activeCustomer?.CustomerID ?? null,
        translations: state.translate && state.translate.translations ? state.translate.translations.activeTranslations || {} : {},
        currentUserID: state.core.login && state.core.login.user && state.core.login.user.UserID ? state.core.login.user.UserID : null,
        creating: state.billing.accounts.creating,
        userLanguage: state?.core?.login?.userSettings?.DefaultLanguage || 'en',
        permissions: state?.core?.login?.permissions || [],
    };
};

const mapDispatchToProps = dispatch => {
    return {
        onCreateBillingAccount: filter => dispatch(actions.createBillingAccount(filter)),
        onListCustomers: params => dispatch(actions.listCustomers(params)),
        onListUsers: activeCustomer => dispatch(actions.listUsers(activeCustomer))
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(styles)(BillingAccountCreate));
